import { useMemo } from "react";

import { PrimaryTirePart } from "../../../../../queries/useSearchTiresByVehicle";
import { useNullableDealerAccountInfo } from "../../../../../stores/hooks/useNullableDealerAccountInfo";
import { createQueryStringToggleHook } from "../../../../../stores/useQueryState";
import { showsApprovedBrandFilter } from "../../../../../util/showsApprovedBrandFilter";

export const approvedBrandParamName = "approved-brand";

export const useApprovedBrand = createQueryStringToggleHook(
  approvedBrandParamName,
  []
);

export const approvedBrandGroups = [
  "star-approved",
  "approved",
  "non-approved",
] as const;

export type ApprovedBrandGroup = (typeof approvedBrandGroups)[number];

/**
 * Star Approved is the original equipment tire (the same rule as the ribbon); otherwise a tire is approved when its
 * brand is on the national account's Tier 1 approved list (TW-1134: for BMW and MINI, the TireLink Tier 1 brands,
 * managed in the CMS).
 */
export const getApprovedBrandGroup = (
  part: Pick<
    PrimaryTirePart,
    "tireManufacturerId" | "tireOriginalEquipmentFlag"
  >,
  approvedManufacturerIds: Set<string>
): ApprovedBrandGroup => {
  if (part.tireOriginalEquipmentFlag) {
    return "star-approved";
  }
  return approvedManufacturerIds.has(String(part.tireManufacturerId))
    ? "approved"
    : "non-approved";
};

/**
 * The national account's Tier 1 approved tire brands, or null when the filter does not apply: not a BMW or MINI
 * dealer, or an API that does not send tier1_approved yet.
 */
export const useApprovedManufacturerIds = (): Set<string> | null => {
  const dealer = useNullableDealerAccountInfo();
  const tireManufacturers = dealer?.dealerProductFilters.tireManufacturers;
  const enabled = showsApprovedBrandFilter(
    dealer?.dealerNationalAccountId ?? ""
  );

  return useMemo(() => {
    const manufacturers = Object.values(tireManufacturers ?? {});
    if (
      !enabled ||
      !manufacturers.some(({ tier1_approved }) => tier1_approved !== undefined)
    ) {
      return null;
    }
    return new Set(
      manufacturers
        .filter(({ tier1_approved }) => tier1_approved)
        .map(({ part_manufacturer_id }) => String(part_manufacturer_id))
    );
  }, [enabled, tireManufacturers]);
};
